import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowRight, X } from 'lucide-react';
import { useState } from 'react';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

function getCompletionTier(score: number) {
  if (score === 100) return { text: 'text-seek', bg: 'bg-seek', bgLight: 'bg-seek/15', border: 'border-seek/30', gradient: 'from-seek/8', ring: 'ring-seek/5', btn: 'bg-seek text-white hover:bg-seek/90' };
  if (score > 80) return { text: 'text-lime-500', bg: 'bg-lime-500', bgLight: 'bg-lime-500/15', border: 'border-lime-500/30', gradient: 'from-lime-500/8', ring: 'ring-lime-500/5', btn: 'bg-lime-500 text-white hover:bg-lime-500/90' };
  if (score > 70) return { text: 'text-yellow-500', bg: 'bg-yellow-500', bgLight: 'bg-yellow-500/15', border: 'border-yellow-500/30', gradient: 'from-yellow-500/8', ring: 'ring-yellow-500/5', btn: 'bg-yellow-500 text-white hover:bg-yellow-500/90' };
  if (score > 60) return { text: 'text-orange-500', bg: 'bg-orange-500', bgLight: 'bg-orange-500/15', border: 'border-orange-500/30', gradient: 'from-orange-500/8', ring: 'ring-orange-500/5', btn: 'bg-orange-500 text-white hover:bg-orange-500/90' };
  return { text: 'text-destructive', bg: 'bg-destructive', bgLight: 'bg-destructive/15', border: 'border-destructive/30', gradient: 'from-destructive/8', ring: 'ring-destructive/5', btn: 'bg-destructive text-destructive-foreground hover:bg-destructive/90' };
}

export function ProfileIncompleteBanner() {
  const { t } = useTranslation();
  const { completionScore, missingItems } = useProfileCompletion();
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  if (dismissed || completionScore >= 100 || location.pathname === '/seek/profile') return null;

  const missingCount = missingItems.length;
  const tier = getCompletionTier(completionScore);

  return (
    <div className={cn('relative w-full mx-auto overflow-hidden rounded-xl border shadow-sm transition-all duration-300 mb-4 max-w-[1200px] 3xl:max-w-[1440px] 4xl:max-w-[1600px]', tier.border, `bg-gradient-to-r ${tier.gradient} via-transparent to-transparent`)}>
      <div className={cn('absolute inset-y-0 start-0 w-1', tier.bg)} />

      <div className="px-4 py-3 ps-5 sm:px-5 sm:py-3.5 sm:ps-6">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          <div className={cn('relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ring-4 shrink-0 mt-0.5 sm:mt-0', tier.bgLight, tier.ring)}>
            <AlertTriangle className={cn('w-3.5 h-3.5 sm:w-4.5 sm:h-4.5', tier.text)} />
            <span className={cn('absolute -top-0.5 -end-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ring-2 ring-background animate-pulse', tier.bg)} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start sm:items-center gap-1.5 sm:gap-2 flex-wrap">
              <p className="font-semibold text-xs sm:text-sm text-foreground leading-tight">
                {t('seek.profile.incompleteBannerTitle')}
              </p>
              <span className={cn('inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider', tier.bgLight, tier.text)}>
                <AlertTriangle className="w-2.5 h-2.5" />
                {completionScore}%
              </span>
            </div>
            <p className="hidden sm:block text-[11px] sm:text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {t('seek.profile.incompleteBannerDesc', { count: missingCount })}
            </p>
          </div>

          <Link to="/seek/profile" className="shrink-0 hidden sm:block">
            <Button
              size="sm"
              className={cn('rounded-full gap-1 text-xs h-7 px-3 shadow-sm font-medium', tier.btn)}
            >
              {t('seek.profile.completeNow')} <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>

          <button
            onClick={() => setDismissed(true)}
            className={cn('p-1 sm:p-1.5 rounded-full transition-colors shrink-0', `${tier.text}/60 hover:${tier.text} hover:${tier.bgLight}`)}
            aria-label={t('common.dismiss')}
          >
            <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
